/**
 * icons.tsx — Minimal stroke icon set for CME (Lucide-style, 1.5px stroke).
 * Ported from the CME Studio prototype (cme/project/app/icons.jsx).
 * Icons use `currentColor` so they inherit color from the parent.
 */
import type { CSSProperties, ReactNode } from "react";

export interface IconProps {
  size?: number;
  strokeWidth?: number;
  style?: CSSProperties;
  className?: string;
  title?: string;
}

interface BaseProps extends IconProps {
  children: ReactNode;
}

function Icon({ size = 16, strokeWidth = 1.5, children, style, ...rest }: BaseProps): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0, display: "block", ...style }}
      {...rest}
    >
      {children}
    </svg>
  );
}

export type IconComponent = (props: IconProps) => JSX.Element;

export const I: Record<string, IconComponent> = {
  // navigation / chrome
  Folder: (p) => (
    <Icon {...p}>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" />
    </Icon>
  ),
  Audit: (p) => (
    <Icon {...p}>
      <path d="M9 4h7l4 4v12a2 2 0 0 1-2 2H9" />
      <path d="M16 4v4h4" />
      <path d="M3 10h6" />
      <path d="M3 14h6" />
      <path d="M3 18h6" />
    </Icon>
  ),
  Strategy: (p) => (
    <Icon {...p}>
      <path d="M3 17l5-5 4 4 8-9" />
      <path d="M14 7h6v6" />
    </Icon>
  ),
  Suppliers: (p) => (
    <Icon {...p}>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20c0-3 2.5-5 6-5s6 2 6 5" />
      <circle cx="17" cy="10" r="2" />
      <path d="M15 20c0-2 1-3.5 3-3.5s3 1.5 3 3.5" />
    </Icon>
  ),
  Sparkle: (p) => (
    <Icon {...p}>
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8" />
    </Icon>
  ),
  Bell: (p) => (
    <Icon {...p}>
      <path d="M6 8a6 6 0 1 1 12 0c0 5 2 6 2 6H4s2-1 2-6Z" />
      <path d="M10 19a2 2 0 0 0 4 0" />
    </Icon>
  ),
  Help: (p) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.8.4-1 1-1 1.7" />
      <circle cx="12" cy="17" r=".5" fill="currentColor" />
    </Icon>
  ),
  Search: (p) => (
    <Icon {...p}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </Icon>
  ),
  Plus: (p) => (
    <Icon {...p}>
      <path d="M12 5v14M5 12h14" />
    </Icon>
  ),
  Minus: (p) => (
    <Icon {...p}>
      <path d="M5 12h14" />
    </Icon>
  ),
  X: (p) => (
    <Icon {...p}>
      <path d="M6 6l12 12M18 6l-12 12" />
    </Icon>
  ),
  Check: (p) => (
    <Icon {...p}>
      <path d="m4 12 5 5 11-11" />
    </Icon>
  ),
  Chevron: (p) => (
    <Icon {...p}>
      <path d="m9 6 6 6-6 6" />
    </Icon>
  ),
  ChevronD: (p) => (
    <Icon {...p}>
      <path d="m6 9 6 6 6-6" />
    </Icon>
  ),
  ChevronU: (p) => (
    <Icon {...p}>
      <path d="m6 15 6-6 6 6" />
    </Icon>
  ),
  Arrow: (p) => (
    <Icon {...p}>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </Icon>
  ),
  ArrowUp: (p) => (
    <Icon {...p}>
      <path d="M12 19V5M6 11l6-6 6 6" />
    </Icon>
  ),
  ArrowDown: (p) => (
    <Icon {...p}>
      <path d="M12 5v14M6 13l6 6 6-6" />
    </Icon>
  ),

  // documents
  PDF: (p) => (
    <Icon {...p}>
      <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v5h5" />
      <path d="M9 13h1.5a1.5 1.5 0 0 1 0 3H9v-3Zm0 3v3" />
      <path d="M14 13v6M14 13h2M14 16h1.5" />
    </Icon>
  ),
  Excel: (p) => (
    <Icon {...p}>
      <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v5h5" />
      <path d="m9 13 5 6M14 13l-5 6" />
    </Icon>
  ),
  DWG: (p) => (
    <Icon {...p}>
      <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v5h5" />
      <path d="M8 13h2v6H8zM12 13h2l1 6M14 13l1 6" />
    </Icon>
  ),
  File: (p) => (
    <Icon {...p}>
      <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v5h5" />
    </Icon>
  ),
  Upload: (p) => (
    <Icon {...p}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="M7 10l5-5 5 5M12 5v12" />
    </Icon>
  ),
  Download: (p) => (
    <Icon {...p}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="M7 10l5 5 5-5M12 15V3" />
    </Icon>
  ),
  Drawing: (p) => (
    <Icon {...p}>
      <path d="M3 4h18v16H3z" />
      <path d="M3 8h18M7 4v16M11 8v12M15 12h6M15 16h6" />
    </Icon>
  ),

  // status
  Alert: (p) => (
    <Icon {...p}>
      <path d="M12 3 2 21h20Z" />
      <path d="M12 10v5" />
      <circle cx="12" cy="18" r=".5" fill="currentColor" />
    </Icon>
  ),
  AlertC: (p) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v4" />
      <circle cx="12" cy="16" r=".5" fill="currentColor" />
    </Icon>
  ),
  Info: (p) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <circle cx="12" cy="8" r=".5" fill="currentColor" />
    </Icon>
  ),
  CheckC: (p) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="m8 12 3 3 5-6" />
    </Icon>
  ),
  Clock: (p) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </Icon>
  ),
  Eye: (p) => (
    <Icon {...p}>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
    </Icon>
  ),

  // actions
  Edit: (p) => (
    <Icon {...p}>
      <path d="M4 20h4l10-10-4-4L4 16v4Z" />
      <path d="M14 6l4 4" />
    </Icon>
  ),
  Trash: (p) => (
    <Icon {...p}>
      <path d="M4 7h16M9 7V4h6v3M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" />
    </Icon>
  ),
  Comment: (p) => (
    <Icon {...p}>
      <path d="M21 12a8 8 0 0 1-11.6 7.1L4 21l1.9-5.4A8 8 0 1 1 21 12Z" />
    </Icon>
  ),
  Send: (p) => (
    <Icon {...p}>
      <path d="m4 12 16-8-6 16-2-7-8-1Z" />
    </Icon>
  ),
  Filter: (p) => (
    <Icon {...p}>
      <path d="M3 5h18l-7 9v6l-4-2v-4Z" />
    </Icon>
  ),
  More: (p) => (
    <Icon {...p}>
      <circle cx="6" cy="12" r="1" fill="currentColor" />
      <circle cx="12" cy="12" r="1" fill="currentColor" />
      <circle cx="18" cy="12" r="1" fill="currentColor" />
    </Icon>
  ),
  External: (p) => (
    <Icon {...p}>
      <path d="M14 4h6v6" />
      <path d="M20 4l-9 9" />
      <path d="M19 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h6" />
    </Icon>
  ),
  Link: (p) => (
    <Icon {...p}>
      <path d="M10 14a3 3 0 0 0 4.2 0l3.6-3.6a3 3 0 0 0-4.2-4.2L12 7.8" />
      <path d="M14 10a3 3 0 0 0-4.2 0l-3.6 3.6a3 3 0 0 0 4.2 4.2L12 16.2" />
    </Icon>
  ),
  Copy: (p) => (
    <Icon {...p}>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V5a2 2 0 0 1 2-2h10" />
    </Icon>
  ),
  Refresh: (p) => (
    <Icon {...p}>
      <path d="M4 4v6h6" />
      <path d="M20 12a8 8 0 1 1-2.3-5.7L20 4" />
    </Icon>
  ),
  Settings: (p) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3h.1a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
    </Icon>
  ),

  // misc
  Building: (p) => (
    <Icon {...p}>
      <path d="M4 21V7l8-4 8 4v14" />
      <path d="M9 21v-6h6v6" />
      <path d="M9 9h2M13 9h2M9 12h2M13 12h2" />
    </Icon>
  ),
  Pin: (p) => (
    <Icon {...p}>
      <path d="M12 22s7-7.5 7-13a7 7 0 1 0-14 0c0 5.5 7 13 7 13Z" />
      <circle cx="12" cy="9" r="2.5" />
    </Icon>
  ),
  Euro: (p) => (
    <Icon {...p}>
      <path d="M18 7a7 7 0 1 0 0 10" />
      <path d="M3 10h11M3 14h11" />
    </Icon>
  ),
  Truck: (p) => (
    <Icon {...p}>
      <path d="M3 17V6h11v11" />
      <path d="M14 9h4l3 4v4h-7" />
      <circle cx="7" cy="18" r="2" />
      <circle cx="17" cy="18" r="2" />
    </Icon>
  ),
  Tag: (p) => (
    <Icon {...p}>
      <path d="M3 12V4a1 1 0 0 1 1-1h8l9 9-9 9-9-9Z" />
      <circle cx="8" cy="8" r="1.5" />
    </Icon>
  ),
  Tools: (p) => (
    <Icon {...p}>
      <path d="M14 7a4 4 0 0 1 5 5l-9 9-5-5 9-9Z" />
      <path d="m14 7-3-3M5 16l-2 2 3 3 2-2" />
    </Icon>
  ),
  Ruler: (p) => (
    <Icon {...p}>
      <path d="M3 8l5-5 13 13-5 5z" />
      <path d="M7 8l2 2M10 5l2 2M11 12l2 2M14 9l2 2M15 16l2 2" />
    </Icon>
  ),
  Layers: (p) => (
    <Icon {...p}>
      <path d="m12 3 9 5-9 5-9-5 9-5Z" />
      <path d="m3 13 9 5 9-5" />
      <path d="m3 18 9 5 9-5" />
    </Icon>
  ),
  Box: (p) => (
    <Icon {...p}>
      <path d="m3 7 9-4 9 4-9 4-9-4Z" />
      <path d="M3 7v10l9 4 9-4V7" />
      <path d="M12 11v10" />
    </Icon>
  ),
  Mail: (p) => (
    <Icon {...p}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </Icon>
  ),
  Sidebar: (p) => (
    <Icon {...p}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M9 4v16" />
    </Icon>
  ),
  Paperclip: (p) => (
    <Icon {...p}>
      <path d="M21 12.5 12.5 21a5.5 5.5 0 1 1-7.8-7.8L13 5a3.5 3.5 0 1 1 5 5l-7.8 7.8a1.5 1.5 0 1 1-2.1-2.1L15 9" />
    </Icon>
  ),
  Inbox: (p) => (
    <Icon {...p}>
      <path d="M3 13h5l1 3h6l1-3h5" />
      <path d="M5 13 7 5h10l2 8v6a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-6Z" />
    </Icon>
  ),
  History: (p) => (
    <Icon {...p}>
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <path d="M3 3v5h5" />
      <path d="M12 8v5l3 2" />
    </Icon>
  ),
  Network: (p) => (
    <Icon {...p}>
      <circle cx="6" cy="6" r="2.4" />
      <circle cx="18" cy="7" r="2.4" />
      <circle cx="12" cy="18" r="2.4" />
      <path d="M7.7 7.6 10.4 16M16.3 8.6 13 16.4M8.3 6.4h7.4" />
    </Icon>
  ),
  Lint: (p) => (
    <Icon {...p}>
      <path d="M3 12h4l2.5-6 4 12 2.5-6h5" />
    </Icon>
  ),
};
